import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#stabilizeMobileSlides();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Horizon hides non-active slideshow slides with content-visibility:hidden.
   * After a product variant change the gallery is replaced with fresh markup, so
   * slide 2 starts hidden. On mobile Safari a native swipe can then land on that
   * unrendered slide and show a white frame. Product galleries are small enough
   * that keeping their slides renderable on mobile is both safe and much more
   * reliable than relying on the visibility observer during a swipe.
   *
   * @param {Element} [gallery=this]
   */
  #stabilizeMobileSlides(gallery = this) {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const slides = gallery.querySelectorAll('slideshow-slide');
    for (const slide of slides) {
      slide.style.setProperty('content-visibility', 'visible', 'important');

      const image = slide.querySelector('img.product-media__image');
      if (image) {
        image.removeAttribute('loading');
      }
    }
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.#stabilizeMobileSlides(newMediaGallery);
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

customElements.define('media-gallery', MediaGallery);
